/**
 * R22 (K2) — `ui.pulse_control {control}` parse (client half of concierge R42 server §29).
 *
 * The GENERALIZED control-pulse cue. When Gigi needs a field OUTSIDE the S7 close flow (an email or
 * phone the normal flow didn't capture), she directs the visitor to a control on the bar ("tap Type
 * above and send me your email") AND the server emits `ui.pulse_control {control}` so the kiosk draws
 * the eye to that ONE control with the same persistent brass-ring breathe the R20 `ui.pulse_card` cue
 * gives the Card control. This is the R20 pattern generalized to any pulseable control.
 *
 * PURE + tolerant (no React / no store / no DOM), so the wire read is unit-testable against captured
 * fixtures / the frozen §29 shape. Like every other `ui.*` reader in this app it resolves the field
 * from BOTH the top level AND a nested `payload`/`data` envelope (the emission-shape ≠ subscription-
 * shape seam that hit `ui.categories` / `ui.contact_field` / `ui.scene`), so an envelope-wrapped cue
 * can never silently no-op.
 *
 * The set of controls the kiosk can pulse is CLOSED (`PULSEABLE_CONTROLS`) — an unknown control value
 * resolves to null (dropped, not pulsed): the server can only draw the eye to a control that exists on
 * the bar.
 *
 * R23 C3 — `card` now JOINS this set. Concierge R43 (G2) broadened the §31 cue so
 * `ui.pulse_control {control:'card'}` fires when Gigi HERSELF directs the visitor to the Card button
 * (not just on visitor card-intent). `card` was excluded at R22 (it rode the legacy §23 `ui.pulse_card`
 * channel); it's now unified into this one code path. The legacy bare `ui.pulse_card` cue is kept as a
 * TOLERATED ALIAS in `useVoice` — it maps to the SAME `card` nonce, so there is exactly one pulse path.
 */

/** The controls this cue may pulse (R23 C3 — `card` joins `type`; unified §31 pulseable set). */
export const PULSEABLE_CONTROLS = ['type', 'card'] as const;
export type PulseableControl = (typeof PULSEABLE_CONTROLS)[number];

export function isPulseableControl(v: unknown): v is PulseableControl {
  return typeof v === 'string' && (PULSEABLE_CONTROLS as readonly string[]).includes(v);
}

/**
 * Resolve the pulsed control name from a raw `ui.pulse_control` message, tolerantly. Reads `control`
 * from, in order: `m.control` (frozen top-level shape, §29) → `m.payload.control` → `m.data.control`.
 * Returns the first VALID PulseableControl, else null (an unknown / missing control is NOT pulsed).
 */
export function parsePulseControl(message: unknown): PulseableControl | null {
  if (!message || typeof message !== 'object') return null;
  const m = message as { control?: unknown; payload?: unknown; data?: unknown };
  if (isPulseableControl(m.control)) return m.control;
  const nestedFrom = (v: unknown): PulseableControl | null => {
    if (!v || typeof v !== 'object') return null;
    const c = (v as { control?: unknown }).control;
    return isPulseableControl(c) ? c : null;
  };
  return nestedFrom(m.payload) ?? nestedFrom(m.data);
}
